import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    CreateSatelliteContactSiteRequestSchema,
    SatelliteContactSiteSchema,
    SatelliteContactSitesStatsResponseSchema,
    UpdateSatelliteContactSiteRequestSchema,
} from "../schemas/satellite-contact-sites";

export interface AdminSatelliteContactSitesListQueryParams extends QueryParams {
    is_active?: boolean;
    search?: string;
    limit?: number;
    cursor?: string;
}

export function createAdminSatelliteContactSitesApi(http: HttpClient, baseUrl: string) {
    const adminSatelliteContactSitesBaseUrl = `${baseUrl}/admin/satellite-contact-sites`;

    return {
        list: createEndpointWithPagination<
            z.infer<typeof SatelliteContactSiteSchema>,
            unknown,
            unknown,
            AdminSatelliteContactSitesListQueryParams
        >(http, "GET", "", {
            base: adminSatelliteContactSitesBaseUrl,
            itemSchema: SatelliteContactSiteSchema,
        }),

        get: createEndpoint<
            z.infer<typeof SatelliteContactSiteSchema>,
            unknown,
            QueryParams,
            { siteId: number | string }
        >(http, "GET", "/:siteId", {
            base: adminSatelliteContactSitesBaseUrl,
            responseSchema: SatelliteContactSiteSchema,
            params: ["siteId"],
        }),

        create: createEndpoint<
            z.infer<typeof SatelliteContactSiteSchema>,
            z.input<typeof CreateSatelliteContactSiteRequestSchema>
        >(http, "POST", "", {
            base: adminSatelliteContactSitesBaseUrl,
            responseSchema: SatelliteContactSiteSchema,
        }),

        update: createEndpoint<
            z.infer<typeof SatelliteContactSiteSchema>,
            z.input<typeof UpdateSatelliteContactSiteRequestSchema>,
            QueryParams,
            { siteId: number | string }
        >(http, "PUT", "/:siteId", {
            base: adminSatelliteContactSitesBaseUrl,
            responseSchema: SatelliteContactSiteSchema,
            params: ["siteId"],
        }),

        delete: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { siteId: number | string }
        >(http, "DELETE", "/:siteId", {
            base: adminSatelliteContactSitesBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["siteId"],
        }),

        regenerateKey: createEndpoint<
            z.infer<typeof SatelliteContactSiteSchema>,
            unknown,
            QueryParams,
            { siteId: number | string }
        >(http, "POST", "/:siteId/regenerate-key", {
            base: adminSatelliteContactSitesBaseUrl,
            responseSchema: SatelliteContactSiteSchema,
            params: ["siteId"],
        }),

        stats: createEndpoint<
            z.infer<typeof SatelliteContactSitesStatsResponseSchema>
        >(http, "GET", "/stats", {
            base: adminSatelliteContactSitesBaseUrl,
            responseSchema: SatelliteContactSitesStatsResponseSchema,
        }),
    };
}

export type AdminSatelliteContactSitesApi = ReturnType<typeof createAdminSatelliteContactSitesApi>;
